import { data, useLoaderData } from 'react-router';
import { excerpt, noIndexMeta, pageMeta } from '@/lib/seo';
import CompetitionPage from '@/routes/competitions/CompetitionPage';
import {
  type Competition,
  EMAIL_PATTERN,
  type LookupResult,
  MOBILE_PATTERN,
  type Results,
  type SearchResult,
} from '@/routes/competitions/types';
import { withinRateLimit } from '../lib/redis.server';
import { callTsum } from '../lib/tsum-api.server';

// Guessing phone numbers to list children: 20 lookups per IP per 10 minutes.
const LOOKUP_LIMIT = 20;
const LOOKUP_WINDOW_SECONDS = 10 * 60;

export function meta({
  loaderData,
  location,
}: {
  loaderData?: { competition?: Competition };
  location: { search: string };
}) {
  const competition = loaderData?.competition;
  if (!competition) return noIndexMeta('Тэмцээн олдсонгүй');
  const path = `/competitions/${competition.id}`;
  // A shared "?tab=register" link should open on that tab; other params are dropped.
  const tab = new URLSearchParams(location.search).get('tab');
  return pageMeta({
    title: competition.name,
    description:
      excerpt(competition.purpose) ??
      excerpt(competition.description) ??
      `${competition.name} — Шинэ Өнөөдөр Сургуулийн тэмцээн. Бүртгүүлэх, бүртгэл болон шалгалтын мэдээллээ шалгах.`,
    path,
    sharePath: tab ? `${path}?tab=${encodeURIComponent(tab)}` : path,
  });
}

export async function loader({ params }: { params: { competitionId?: string } }) {
  const competitions = await callTsum<Competition[]>('competitions');
  const competition = competitions?.find((c) => String(c.id) === params.competitionId);
  if (!competition) throw data('Тэмцээн олдсонгүй.', { status: 404 });
  // Results (CONTEXT.md) only once published; a failed fetch just hides them.
  const results = competition.results_published
    ? await callTsum<Results>('competition-results', {
        method: 'POST',
        body: { competitionId: competition.id },
      })
    : null;
  return { competition, results };
}

function clientIp(request: Request): string {
  return (
    request.headers.get('x-forwarded-for')?.split(',')[0]?.trim() ||
    request.headers.get('x-real-ip') ||
    'unknown'
  );
}

// POST — Participant lookup (CONTEXT.md). Checked here too, then forwarded to
// tsum with the server-side api-key (the browser never sees it).
export async function action({
  request,
  params,
}: {
  request: Request;
  params: { competitionId?: string };
}): Promise<LookupResult> {
  const body = await request.json().catch(() => ({}));
  const mode = body.mode === 'email' ? 'email' : 'mobile';
  const value = String(body.value ?? '').trim();

  if (mode === 'mobile' && !MOBILE_PATTERN.test(value)) {
    return { ok: false, error: 'Утасны дугаар 8 оронтой байна.' };
  }
  if (mode === 'email' && !EMAIL_PATTERN.test(value)) {
    return { ok: false, error: 'И-мэйл хаяг буруу байна.' };
  }
  if (
    !(await withinRateLimit(`lookup:${clientIp(request)}`, LOOKUP_LIMIT, LOOKUP_WINDOW_SECONDS))
  ) {
    return { ok: false, error: 'Хэт олон удаа хайлаа. Түр хүлээгээд дахин оролдоно уу.' };
  }

  const result = await callTsum<SearchResult>('competition-search', {
    method: 'POST',
    body: {
      competitionId: Number(params.competitionId),
      ...(mode === 'mobile' ? { mobile: value } : { email: value.toLowerCase() }),
    },
  });
  if (!result) return { ok: false, error: 'Хайлт хийхэд алдаа гарлаа. Дахин оролдоно уу.' };
  return { ok: true, result };
}

export default function CompetitionRoute() {
  const { competition, results } = useLoaderData<typeof loader>();
  return <CompetitionPage competition={competition} results={results} />;
}
